import React, { useState } from 'react';
import { cn } from '../lib/utils';

// UI Components
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger } from './ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import { Toggle } from './ui/toggle';
import { Progress } from './ui/progress';
import { Separator } from './ui/separator';
import { Skeleton } from './ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { AspectRatio } from './ui/aspect-ratio';
import { Calendar } from './ui/calendar';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from './ui/context-menu';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from './ui/drawer';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';
import { Menubar, MenubarContent, MenubarItem, MenubarMenu, MenubarSeparator, MenubarShortcut, MenubarTrigger } from './ui/menubar';
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from './ui/navigation-menu';
import { ScrollArea } from './ui/scroll-area';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, SidebarSeparator } from './ui/sidebar';
import { Toaster } from './ui/sonner';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ToggleGroup, ToggleGroupItem } from './ui/toggle-group';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from './ui/pagination';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from './ui/resizable';

// Custom Components
import { AuthModal } from './AuthModal';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Footer } from './Footer';
import { ThemeToggle } from './theme-toggle';
import { Icon } from './ui/icon';

// Icons
import { 
  Info, 
  XCircle, 
  User, 
  Settings, 
  Home,
  DollarSign,
  Search,
  Filter,
  Bell,
  Mail,
  Star,
  Heart,
  Share,
  Bookmark,
  Clock,
  MapPin,
  Phone,
  Globe,
  Lock,
  LogOut,
  Shield,
  Zap,
  Target,
  BarChart3,
  Activity,
  Users,
  CreditCard,
  Wallet,
  Building2,
  Car,
  Home as HomeIcon,
  Briefcase,
  GraduationCap,
  Baby,
  Plane,
  Camera,
  Music,
  Utensils,
  ShoppingBag,
  Gift,
  Heart as HeartIcon,
  Smile,
  ThumbsUp,
  MessageCircle,
  Send,
  Edit,
  Trash2,
  Copy,
  Link,
  ExternalLink,
  Maximize2,
  Minimize2,
  RotateCcw,
  RefreshCw,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Mic,
  MicOff,
  Video,
  VideoOff,
  Wifi,
  WifiOff,
  Battery,
  Moon,
  Sun,
  Cloud,
  CloudRain,
  CloudSnow,
  Wind,
  Thermometer,
  Umbrella,
  Snowflake,
  Droplets,
  Waves,
  Mountain,
  Leaf,
  Flower,
  Bug,
  Bird,
  Fish,
  Cat,
  Dog,
  X,
  AlertTriangle,
  Bold,
  Italic,
  Underline,
  Loader2,
  ChevronDown,
  Minus,
  Plus,
  Download,
  Upload,
  Save,
  MoreHorizontal,
  FileText,
  FolderOpen,
  Printer,
  UserPlus,
  UserMinus,
  Columns,
  Check,
  BookOpen
} from 'lucide-react';

// ListItem Component for Navigation Menu
const ListItem = React.forwardRef<
  React.ElementRef<"a">,
  React.ComponentPropsWithoutRef<"a">
>(({ className, title, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
            className
          )}
          {...props}
        >
          <div className="text-sm font-medium leading-none">{title}</div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {children}
          </p>
        </a>
      </NavigationMenuLink>
    </li>
  )
})
ListItem.displayName = "ListItem"

// Simple Loading Spinner Component
function SimpleLoadingSpinner() {
  return (
    <div className="flex items-center justify-center p-4">
      <div className="flex flex-col items-center space-y-2">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Загрузка...</p>
      </div>
    </div>
  );
}

const ComponentsShowcase: React.FC = () => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [progress, setProgress] = useState(13);
  const [isChecked, setIsChecked] = useState(false);
  const [isToggled, setIsToggled] = useState(false);
  const [selectedOption, setSelectedOption] = useState("option1");
  const [selectedColumns, setSelectedColumns] = useState<string[]>(["Name", "Email", "Status", "Amount"]);

  React.useEffect(() => {
    const timer = setTimeout(() => setProgress(66), 500);
    return () => clearTimeout(timer);
  }, []);

  // Prevent auto-scroll on page load
  React.useEffect(() => {
    // Ensure page starts at the top and prevent any auto-scroll
    window.scrollTo(0, 0);
    
    // Remove focus from any element that might cause auto-scroll
    if (document.activeElement) {
      (document.activeElement as HTMLElement).blur();
    }
    
    // Prevent any element from getting focus on page load
    const preventFocus = (e: FocusEvent) => {
      if (e.target && (e.target as HTMLElement).closest('.carousel-section')) {
        e.preventDefault();
        (e.target as HTMLElement).blur();
      }
    };
    
    document.addEventListener('focusin', preventFocus);
    
    return () => {
      document.removeEventListener('focusin', preventFocus);
    };
  }, []);

  const sampleData = [
    {
      name: "Total Revenue",
      total: "$45,231.89",
      change: "+20.1%",
      icon: DollarSign,
    },
    {
      name: "Subscriptions",
      total: "+2350",
      change: "+180.1%",
      icon: Users,
    },
    {
      name: "Sales",
      total: "+12,234",
      change: "+19%",
      icon: CreditCard,
    },
    {
      name: "Active Now",
      total: "+573",
      change: "+201",
      icon: Activity,
    },
  ];

  return (
    <div className="min-h-screen bg-background p-8" style={{ scrollBehavior: 'auto' }}>
      <div className="max-w-7xl mx-auto">
        {/* Library Name and Documentation Link */}
        <div className="mb-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-semibold text-foreground">
                shadcn/ui
              </h2>
              <Badge variant="secondary" className="text-sm">
                UI Component Library
              </Badge>
            </div>
            <a
              href="https://ui.shadcn.com/docs/components"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
            >
              <BookOpen className="h-4 w-4" />
              <span>Документация</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
          <h1 className="text-4xl font-bold text-foreground mb-2">
            BetterPension Components Showcase
          </h1>
          <p className="text-muted-foreground text-lg">
            Демонстрация всех компонентов системы для проверки корректности отображения
          </p>
            </div>
            <ThemeToggle />
          </div>
          <Breadcrumb className="mt-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Главная</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Компоненты</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Theme Toggle */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Theme Toggle</CardTitle>
            <CardDescription>Переключатель между светлой и темной темой</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-4">
              <ThemeToggle />
              <div className="text-sm text-muted-foreground">
                Нажмите на кнопку для переключения темы. Доступны варианты: Светлая, Темная, Системная.
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Language Switcher */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Language Switcher</CardTitle>
            <CardDescription>Переключатель языков интерфейса</CardDescription>
          </CardHeader>
          <CardContent>
            {/* LanguageSwitcher temporarily disabled for testing */}
            {/* <LanguageSwitcher /> */}
            <div className="p-4 border rounded">
              <p className="text-muted-foreground">Language Switcher Component</p>
            </div>
          </CardContent>
        </Card>

        {/* Accordion */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Accordion</CardTitle>
            <CardDescription>Сворачиваемые секции контента</CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>Is it accessible?</AccordionTrigger>
                <AccordionContent>
                  Yes. It adheres to the WAI-ARIA design pattern.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger>Is it styled?</AccordionTrigger>
                <AccordionContent>
                  Yes. It comes with default styles that matches the other components&apos; aesthetic.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger>Is it animated?</AccordionTrigger>
                <AccordionContent>
                  Yes. It&apos;s animated by default, but you can disable it if you prefer.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Alert Dialog */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Alert Dialog</CardTitle>
            <CardDescription>Диалоговые окна с подтверждением действий</CardDescription>
          </CardHeader>
          <CardContent>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline">Delete Account</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete your account
                    and remove your data from our servers.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction>Continue</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        {/* Aspect Ratio */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Aspect Ratio</CardTitle>
            <CardDescription>Поддержание пропорций для изображений и видео</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>16:9 Aspect Ratio</Label>
                <AspectRatio ratio={16 / 9} className="bg-muted">
                  <div className="flex items-center justify-center text-muted-foreground">
                    16:9
                  </div>
                </AspectRatio>
              </div>
              <div className="space-y-2">
                <Label>4:3 Aspect Ratio</Label>
                <AspectRatio ratio={4 / 3} className="bg-muted">
                  <div className="flex items-center justify-center text-muted-foreground">
                    4:3
                  </div>
                </AspectRatio>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calendar */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Calendar</CardTitle>
            <CardDescription>Календарь для выбора дат</CardDescription>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={new Date()}
              className="rounded-md border"
            />
          </CardContent>
        </Card>

        {/* Carousel */}
        <Card className="mb-8 carousel-section">
          <CardHeader>
            <CardTitle>Carousel</CardTitle>
            <CardDescription>Карусель для прокрутки контента</CardDescription>
          </CardHeader>
          <CardContent>
            <Carousel 
              className="w-full max-w-xs" 
              tabIndex={-1} 
              onFocus={(e) => e.target.blur()}
              onMouseEnter={() => {
                // Remove focus from all carousel elements
                const carouselElements = document.querySelectorAll('.carousel-section button, .carousel-section [tabindex]');
                carouselElements.forEach(el => (el as HTMLElement).blur());
              }}
            >
              <CarouselContent>
                {Array.from({ length: 5 }).map((_, index) => (
                  <CarouselItem key={index}>
                    <div className="p-1">
                      <Card>
                        <CardContent className="flex aspect-square items-center justify-center p-6">
                          <span className="text-4xl font-semibold">{index + 1}</span>
                        </CardContent>
                      </Card>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious tabIndex={-1} />
              <CarouselNext tabIndex={-1} />
            </Carousel>
          </CardContent>
        </Card>

        {/* Collapsible */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Collapsible</CardTitle>
            <CardDescription>Сворачиваемый контент</CardDescription>
          </CardHeader>
          <CardContent>
            <Collapsible className="w-full space-y-2">
              <div className="flex items-center justify-between space-x-4 px-4">
                <h4 className="text-sm font-semibold">@peduarte starred 3 repositories</h4>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-9 p-0">
                    <ChevronDown className="h-4 w-4" />
                    <span className="sr-only">Toggle</span>
                  </Button>
                </CollapsibleTrigger>
              </div>
              <div className="rounded-md border px-4 py-3 font-mono text-sm">
                @radix-ui/primitives
              </div>
              <CollapsibleContent className="space-y-2">
                <div className="rounded-md border px-4 py-3 font-mono text-sm">
                  @radix-ui/colors
                </div>
                <div className="rounded-md border px-4 py-3 font-mono text-sm">
                  @stitches/react
                </div>
              </CollapsibleContent>
            </Collapsible>
          </CardContent>
        </Card>

        {/* Command */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Command</CardTitle>
            <CardDescription>Командная палитра для поиска и навигации</CardDescription>
          </CardHeader>
          <CardContent>
            <Command className="rounded-lg border shadow-md">
              <CommandInput placeholder="Type a command or search..." />
              <CommandList>
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandGroup heading="Suggestions">
                  <CommandItem>Calendar</CommandItem>
                  <CommandItem>Search Emoji</CommandItem>
                  <CommandItem>Calculator</CommandItem>
                </CommandGroup>
                <CommandGroup heading="Settings">
                  <CommandItem>Profile</CommandItem>
                  <CommandItem>Billing</CommandItem>
                  <CommandItem>Settings</CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </CardContent>
        </Card>

        {/* Context Menu */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Context Menu</CardTitle>
            <CardDescription>Контекстное меню по правому клику</CardDescription>
          </CardHeader>
          <CardContent>
            <ContextMenu>
              <ContextMenuTrigger className="flex h-[150px] w-[300px] items-center justify-center rounded-md border border-dashed text-sm">
                Right click here
              </ContextMenuTrigger>
              <ContextMenuContent className="w-64">
                <ContextMenuItem inset>
                  Back
                </ContextMenuItem>
                <ContextMenuItem inset disabled>
                  Forward
                </ContextMenuItem>
                <ContextMenuItem inset>
                  Reload
                </ContextMenuItem>
                <ContextMenuSeparator />
                <ContextMenuItem inset>
                  Save Page As...
                </ContextMenuItem>
                <ContextMenuItem inset>
                  Create Shortcut...
                </ContextMenuItem>
                <ContextMenuItem inset>
                  Name Window...
                </ContextMenuItem>
                <ContextMenuSeparator />
                <ContextMenuItem inset>
                  Developer Tools
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu>
          </CardContent>
        </Card>

        {/* Drawer */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Drawer</CardTitle>
            <CardDescription>Выдвижная панель для мобильных устройств</CardDescription>
          </CardHeader>
          <CardContent>
            <Drawer>
              <DrawerTrigger asChild>
                <Button variant="outline">Open Drawer</Button>
              </DrawerTrigger>
              <DrawerContent>
                <div className="mx-auto w-full max-w-sm">
                  <DrawerHeader>
                    <DrawerTitle>Move Goal</DrawerTitle>
                    <DrawerDescription>Set your daily activity goal.</DrawerDescription>
                  </DrawerHeader>
                  <div className="p-4 pb-0">
                    <div className="flex items-center justify-center space-x-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 shrink-0 rounded-full"
                      >
                        <Minus className="h-4 w-4" />
                        <span className="sr-only">Decrease</span>
                      </Button>
                      <div className="flex-1 text-center">
                        <div className="text-7xl font-bold tracking-tighter">
                          0
                        </div>
                        <div className="text-xs text-muted-foreground uppercase">
                          Calories/day
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 shrink-0 rounded-full"
                      >
                        <Plus className="h-4 w-4" />
                        <span className="sr-only">Increase</span>
                      </Button>
                    </div>
                    <div className="mt-3 h-[120px]">
                      <Progress value={0} className="h-full" />
                    </div>
                  </div>
                  <DrawerFooter>
                    <Button>Submit</Button>
                    <DrawerClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DrawerClose>
                  </DrawerFooter>
                </div>
              </DrawerContent>
            </Drawer>
          </CardContent>
        </Card>

        {/* Hover Card */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Hover Card</CardTitle>
            <CardDescription>Карточка с дополнительной информацией при наведении</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
            <HoverCard>
              <HoverCardTrigger asChild>
                <Button variant="link">@nextjs</Button>
              </HoverCardTrigger>
              <HoverCardContent className="w-80">
                <div className="flex justify-between space-x-4">
                  <Avatar>
                    <AvatarImage src="https://github.com/vercel.png" />
                    <AvatarFallback>VC</AvatarFallback>
                  </Avatar>
                  <div className="space-y-1">
                    <h4 className="text-sm font-semibold">@nextjs</h4>
                    <p className="text-sm">
                      The React Framework for Production.
                    </p>
                    <div className="flex items-center pt-2">
                        <Clock className="mr-2 h-4 w-4 opacity-70" />
                      <span className="text-xs text-muted-foreground">
                        Joined December 2021
                      </span>
                    </div>
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>

              <HoverCard>
                <HoverCardTrigger asChild>
                  <Button variant="link">@shadcn</Button>
                </HoverCardTrigger>
                <HoverCardContent className="w-80">
                  <div className="flex justify-between space-x-4">
                    <Avatar>
                      <AvatarImage src="https://github.com/shadcn.png" />
                      <AvatarFallback>SC</AvatarFallback>
                    </Avatar>
                    <div className="space-y-1">
                      <h4 className="text-sm font-semibold">@shadcn</h4>
                      <p className="text-sm">
                        Beautifully designed components built with Radix UI and Tailwind CSS.
                      </p>
                      <div className="flex items-center pt-2">
                        <Star className="mr-2 h-4 w-4 opacity-70" />
                        <span className="text-xs text-muted-foreground">
                          92.3k stars on GitHub
                        </span>
                      </div>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>

              <HoverCard>
                <HoverCardTrigger asChild>
                  <Button variant="link">@betterpension</Button>
                </HoverCardTrigger>
                <HoverCardContent className="w-80">
                  <div className="flex justify-between space-x-4">
                    <Avatar>
                      <AvatarImage src="https://github.com/betterpension.png" />
                      <AvatarFallback>BP</AvatarFallback>
                    </Avatar>
                    <div className="space-y-1">
                      <h4 className="text-sm font-semibold">@betterpension</h4>
                      <p className="text-sm">
                        Инновационная платформа для управления пенсионными накоплениями.
                      </p>
                      <div className="flex items-center pt-2">
                        <Users className="mr-2 h-4 w-4 opacity-70" />
                        <span className="text-xs text-muted-foreground">
                          Активные пользователи: 1,234
                        </span>
                      </div>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
          </CardContent>
        </Card>

        {/* Input OTP */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Input OTP</CardTitle>
            <CardDescription>Поле ввода для одноразовых паролей</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="otp">Enter your OTP</Label>
              <InputOTP maxLength={6} id="otp">
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
              <p className="text-xs text-muted-foreground">
                Enter the 6-digit code sent to your phone.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Menubar */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Menubar</CardTitle>
            <CardDescription>Горизонтальное меню с выпадающими списками</CardDescription>
          </CardHeader>
          <CardContent>
            <Menubar>
              <MenubarMenu>
                <MenubarTrigger>File</MenubarTrigger>
                <MenubarContent>
                  <MenubarItem>
                    New Tab <MenubarShortcut>⌘T</MenubarShortcut>
                  </MenubarItem>
                  <MenubarItem>
                    New Window <MenubarShortcut>⌘N</MenubarShortcut>
                  </MenubarItem>
                  <MenubarItem disabled>New Incognito Window</MenubarItem>
                  <MenubarSeparator />
                  <MenubarItem>
                    Share
                    <MenubarShortcut>⌘S</MenubarShortcut>
                  </MenubarItem>
                </MenubarContent>
              </MenubarMenu>
              <MenubarMenu>
                <MenubarTrigger>Edit</MenubarTrigger>
                <MenubarContent>
                  <MenubarItem>
                    Undo <MenubarShortcut>⌘Z</MenubarShortcut>
                  </MenubarItem>
                  <MenubarItem>
                    Redo <MenubarShortcut>⇧⌘Z</MenubarShortcut>
                  </MenubarItem>
                  <MenubarSeparator />
                  <MenubarItem>
                    Cut <MenubarShortcut>⌘X</MenubarShortcut>
                  </MenubarItem>
                  <MenubarItem>
                    Copy <MenubarShortcut>⌘C</MenubarShortcut>
                  </MenubarItem>
                  <MenubarItem>
                    Paste <MenubarShortcut>⌘V</MenubarShortcut>
                  </MenubarItem>
                </MenubarContent>
              </MenubarMenu>
              <MenubarMenu>
                <MenubarTrigger>View</MenubarTrigger>
                <MenubarContent>
                  <MenubarItem>
                    Zoom In <MenubarShortcut>⌘+</MenubarShortcut>
                  </MenubarItem>
                  <MenubarItem>
                    Zoom Out <MenubarShortcut>⌘-</MenubarShortcut>
                  </MenubarItem>
                  <MenubarSeparator />
                  <MenubarItem>
                    Toggle Fullscreen <MenubarShortcut>⌘F</MenubarShortcut>
                  </MenubarItem>
                </MenubarContent>
              </MenubarMenu>
            </Menubar>
          </CardContent>
        </Card>

        {/* Navigation Menu */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Navigation Menu</CardTitle>
            <CardDescription>Навигационное меню с выпадающими списками</CardDescription>
          </CardHeader>
          <CardContent>
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger>Getting started</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid gap-3 p-6 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
                      <li className="row-span-3">
                        <NavigationMenuLink asChild>
                          <a
                            className="flex h-full w-full select-none flex-col justify-end rounded-md bg-gradient-to-b from-muted/50 to-muted p-6 no-underline outline-none focus:shadow-md"
                            href="/"
                          >
                            <div className="mb-2 mt-4 text-lg font-medium">
                              shadcn/ui
                            </div>
                            <p className="text-sm leading-tight text-muted-foreground">
                              Beautifully designed components built with Radix UI and Tailwind CSS.
                            </p>
                          </a>
                        </NavigationMenuLink>
                      </li>
                      <ListItem href="/docs" title="Introduction">
                        Re-usable components built using Radix UI and Tailwind CSS.
                      </ListItem>
                      <ListItem href="/docs/installation" title="Installation">
                        How to install dependencies and structure your app.
                      </ListItem>
                      <ListItem href="/docs/primitives/typography" title="Typography">
                        Styles for headings, paragraphs, lists...etc
                      </ListItem>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuTrigger>Components</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                      {[
                        {
                          title: "Alert Dialog",
                          href: "/docs/primitives/alert-dialog",
                          description:
                            "A modal dialog that interrupts the user with important content and expects a response.",
                        },
                        {
                          title: "Hover Card",
                          href: "/docs/primitives/hover-card",
                          description:
                            "For sighted users to preview content available behind a link.",
                        },
                        {
                          title: "Progress",
                          href: "/docs/primitives/progress",
                          description:
                            "Displays an indicator showing the completion progress of a task.",
                        },
                        {
                          title: "Scroll-area",
                          href: "/docs/primitives/scroll-area",
                          description: "Visually or semantically separates content.",
                        },
                        {
                          title: "Tabs",
                          href: "/docs/primitives/tabs",
                          description:
                            "A set of layered sections of content—known as tab panels—that are displayed one at a time.",
                        },
                        {
                          title: "Tooltip",
                          href: "/docs/primitives/tooltip",
                          description:
                            "A popup that displays information related to an element when the element receives keyboard focus or the mouse hovers over it.",
                        },
                      ].map((component) => (
                        <ListItem
                          key={component.title}
                          title={component.title}
                          href={component.href}
                        >
                          {component.description}
                        </ListItem>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()}>
                    Documentation
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </CardContent>
        </Card>

        {/* Scroll Area */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Scroll Area</CardTitle>
            <CardDescription>Область с прокруткой</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px] w-[350px] rounded-md border p-4">
              <div className="space-y-4">
                <h4 className="text-sm font-medium leading-none">Tags</h4>
                <div className="text-sm text-muted-foreground">
                  <p>@radix-ui/react-accordion</p>
                  <p>@radix-ui/react-alert-dialog</p>
                  <p>@radix-ui/react-aspect-ratio</p>
                  <p>@radix-ui/react-avatar</p>
                  <p>@radix-ui/react-checkbox</p>
                  <p>@radix-ui/react-collapsible</p>
                  <p>@radix-ui/react-context-menu</p>
                  <p>@radix-ui/react-dialog</p>
                  <p>@radix-ui/react-dropdown-menu</p>
                  <p>@radix-ui/react-hover-card</p>
                  <p>@radix-ui/react-label</p>
                  <p>@radix-ui/react-menubar</p>
                  <p>@radix-ui/react-navigation-menu</p>
                  <p>@radix-ui/react-popover</p>
                  <p>@radix-ui/react-progress</p>
                  <p>@radix-ui/react-radio-group</p>
                  <p>@radix-ui/react-scroll-area</p>
                  <p>@radix-ui/react-select</p>
                  <p>@radix-ui/react-separator</p>
                  <p>@radix-ui/react-slider</p>
                  <p>@radix-ui/react-slot</p>
                  <p>@radix-ui/react-switch</p>
                  <p>@radix-ui/react-tabs</p>
                  <p>@radix-ui/react-toast</p>
                  <p>@radix-ui/react-toggle</p>
                  <p>@radix-ui/react-tooltip</p>
                </div>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Sheet */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Sheet</CardTitle>
            <CardDescription>Боковая панель</CardDescription>
          </CardHeader>
          <CardContent>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline">Open Sheet</Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Edit profile</SheetTitle>
                  <SheetDescription>
                    Make changes to your profile here. Click save when you're done.
                  </SheetDescription>
                </SheetHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="name" className="text-right">
                      Name
                    </Label>
                    <Input
                      id="name"
                      defaultValue="Pedro Duarte"
                      className="col-span-3"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="username" className="text-right">
                      Username
                    </Label>
                    <Input
                      id="username"
                      defaultValue="@peduarte"
                      className="col-span-3"
                    />
                  </div>
                </div>
                <SheetFooter>
                  <SheetClose asChild>
                    <Button type="submit">Save changes</Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </CardContent>
        </Card>

        {/* Sidebar */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Sidebar</CardTitle>
            <CardDescription>Боковая панель навигации</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Sidebar temporarily disabled for testing */}
            {/* <div className="h-[400px] border rounded-lg">
              <Sidebar>
                <SidebarHeader>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton>
                        <User className="h-4 w-4" />
                        <span>Profile</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarHeader>
                <SidebarContent>
                  <SidebarGroup>
                    <SidebarGroupLabel>Menu</SidebarGroupLabel>
                    <SidebarGroupContent>
                      <SidebarMenu>
                        <SidebarMenuItem>
                          <SidebarMenuButton>
                            <Home className="h-4 w-4" />
                            <span>Home</span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                        <SidebarMenuItem>
                          <SidebarMenuButton>
                            <Settings className="h-4 w-4" />
                            <span>Settings</span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                        <SidebarMenuItem>
                          <SidebarMenuButton>
                            <Mail className="h-4 w-4" />
                            <span>Messages</span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </SidebarGroup>
                </SidebarContent>
                <SidebarFooter>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton>
                        <LogOut className="h-4 w-4" />
                        <span>Logout</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarFooter>
              </Sidebar>
            </div> */}
            <div className="p-4 border rounded">
              <p className="text-muted-foreground">Sidebar Component</p>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Table</CardTitle>
            <CardDescription>Таблица для отображения данных</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableCaption>A list of your recent invoices.</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Invoice</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">INV001</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Credit Card</TableCell>
                  <TableCell className="text-right">$250.00</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">INV002</TableCell>
                  <TableCell>Pending</TableCell>
                  <TableCell>PayPal</TableCell>
                  <TableCell className="text-right">$150.00</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">INV003</TableCell>
                  <TableCell>Unpaid</TableCell>
                  <TableCell>Bank Transfer</TableCell>
                  <TableCell className="text-right">$350.00</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">INV004</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Credit Card</TableCell>
                  <TableCell className="text-right">$450.00</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">INV005</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>PayPal</TableCell>
                  <TableCell className="text-right">$550.00</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">INV006</TableCell>
                  <TableCell>Pending</TableCell>
                  <TableCell>Bank Transfer</TableCell>
                  <TableCell className="text-right">$200.00</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Toggle Group */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Toggle Group</CardTitle>
            <CardDescription>Группа переключателей</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Single Selection</Label>
              <ToggleGroup type="single" aria-label="Text alignment">
                <ToggleGroupItem value="left" aria-label="Left aligned">
                  <Bold className="h-4 w-4" />
                </ToggleGroupItem>
                <ToggleGroupItem value="center" aria-label="Center aligned">
                  <Italic className="h-4 w-4" />
                </ToggleGroupItem>
                <ToggleGroupItem value="right" aria-label="Right aligned">
                  <Underline className="h-4 w-4" />
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
            <div className="space-y-2">
              <Label>Multiple Selection</Label>
              <ToggleGroup type="multiple" aria-label="Text formatting">
                <ToggleGroupItem value="bold" aria-label="Toggle bold">
                  <Bold className="h-4 w-4" />
                </ToggleGroupItem>
                <ToggleGroupItem value="italic" aria-label="Toggle italic">
                  <Italic className="h-4 w-4" />
                </ToggleGroupItem>
                <ToggleGroupItem value="underline" aria-label="Toggle underline">
                  <Underline className="h-4 w-4" />
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </CardContent>
        </Card>

        {/* Buttons Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Buttons</CardTitle>
            <CardDescription>Различные типы кнопок с иконками</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* All Button Variants in Three Sizes */}
            <div className="space-y-4">
              <Label>Все варианты кнопок в трех размерах</Label>
              
              {/* Default Variant */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Default</div>
            <div className="flex flex-wrap gap-4">
              <Button size="sm">Small</Button>
              <Button size="default">Default</Button>
              <Button size="lg">Large</Button>
            </div>
              </div>

              {/* Secondary Variant */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Secondary</div>
            <div className="flex flex-wrap gap-4">
                  <Button variant="secondary" size="sm">Small</Button>
                  <Button variant="secondary" size="default">Default</Button>
                  <Button variant="secondary" size="lg">Large</Button>
                </div>
              </div>

              {/* Destructive Variant */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Destructive</div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="destructive" size="sm">Small</Button>
                  <Button variant="destructive" size="default">Default</Button>
                  <Button variant="destructive" size="lg">Large</Button>
                </div>
              </div>

              {/* Outline Variant */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Outline</div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="outline" size="sm">Small</Button>
                  <Button variant="outline" size="default">Default</Button>
                  <Button variant="outline" size="lg">Large</Button>
                </div>
              </div>

              {/* Ghost Variant */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Ghost</div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="ghost" size="sm">Small</Button>
                  <Button variant="ghost" size="default">Default</Button>
                  <Button variant="ghost" size="lg">Large</Button>
                </div>
              </div>

              {/* Link Variant */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Link</div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="link" size="sm">Small</Button>
                  <Button variant="link" size="default">Default</Button>
                  <Button variant="link" size="lg">Large</Button>
                </div>
              </div>
            </div>

            {/* Buttons with Icons - All Sizes */}
            <div className="space-y-4">
              <Label>Кнопки с иконками - все размеры</Label>
              
              {/* Default with Icon */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Default</div>
                <div className="flex flex-wrap gap-4">
                  <Button size="sm">
                    <Icon icon={User} size="sm" className="mr-2" />
                    Profile
                  </Button>
                  <Button size="default">
                    <Icon icon={User} size="sm" className="mr-2" />
                    Profile
                  </Button>
                  <Button size="lg">
                    <Icon icon={User} size="sm" className="mr-2" />
                    Profile
                  </Button>
                </div>
              </div>

              {/* Secondary with Icon */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Secondary</div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="secondary" size="sm">
                    <Icon icon={Settings} size="sm" className="mr-2" />
                    Settings
                  </Button>
                  <Button variant="secondary" size="default">
                    <Icon icon={Settings} size="sm" className="mr-2" />
                    Settings
                  </Button>
                  <Button variant="secondary" size="lg">
                    <Icon icon={Settings} size="sm" className="mr-2" />
                    Settings
                  </Button>
                </div>
              </div>

              {/* Outline with Icon */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Outline</div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="outline" size="sm">
                    <Icon icon={Mail} size="sm" className="mr-2" />
                    Messages
                  </Button>
                  <Button variant="outline" size="default">
                    <Icon icon={Mail} size="sm" className="mr-2" />
                    Messages
                  </Button>
                  <Button variant="outline" size="lg">
                    <Icon icon={Mail} size="sm" className="mr-2" />
                    Messages
                  </Button>
                </div>
              </div>

              {/* Ghost with Icon */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Ghost</div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="ghost" size="sm">
                    <Icon icon={Bell} size="sm" className="mr-2" />
                    Notifications
                  </Button>
                  <Button variant="ghost" size="default">
                    <Icon icon={Bell} size="sm" className="mr-2" />
                    Notifications
                  </Button>
                  <Button variant="ghost" size="lg">
                    <Icon icon={Bell} size="sm" className="mr-2" />
                    Notifications
                  </Button>
                </div>
              </div>

              {/* Destructive with Icon */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Destructive</div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="destructive" size="sm">
                    <Icon icon={Trash2} size="sm" className="mr-2" />
                    Delete
                  </Button>
                  <Button variant="destructive" size="default">
                    <Icon icon={Trash2} size="sm" className="mr-2" />
                    Delete
                  </Button>
                  <Button variant="destructive" size="lg">
                    <Icon icon={Trash2} size="sm" className="mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>

            {/* Icon Only Buttons - All Sizes */}
            <div className="space-y-4">
              <Label>Кнопки только с иконками - все размеры</Label>
              
              {/* Default Icon Only */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Default</div>
                <div className="flex flex-wrap gap-4">
                  <Button size="icon">
                    <Icon icon={User} size="sm" />
                  </Button>
                  <Button size="icon">
                    <Icon icon={Settings} size="sm" />
                  </Button>
                  <Button size="icon">
                    <Icon icon={Mail} size="sm" />
                  </Button>
                </div>
              </div>

              {/* Outline Icon Only */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Outline</div>
                <div className="flex flex-wrap gap-4">
                  <Button size="icon" variant="outline">
                    <Icon icon={Bell} size="sm" />
                  </Button>
                  <Button size="icon" variant="outline">
                    <Icon icon={Search} size="sm" />
                  </Button>
                  <Button size="icon" variant="outline">
                    <Icon icon={Filter} size="sm" />
                  </Button>
                </div>
              </div>

              {/* Secondary Icon Only */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Secondary</div>
                <div className="flex flex-wrap gap-4">
                  <Button size="icon" variant="secondary">
                    <Icon icon={Heart} size="sm" />
                  </Button>
                  <Button size="icon" variant="secondary">
                    <Icon icon={Share} size="sm" />
                  </Button>
                  <Button size="icon" variant="secondary">
                    <Icon icon={Bookmark} size="sm" />
                  </Button>
                </div>
              </div>

              {/* Ghost Icon Only */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Ghost</div>
                <div className="flex flex-wrap gap-4">
                  <Button size="icon" variant="ghost">
                    <Icon icon={Plus} size="sm" />
                  </Button>
                  <Button size="icon" variant="ghost">
                    <Icon icon={Edit} size="sm" />
                  </Button>
                  <Button size="icon" variant="ghost">
                    <Icon icon={Trash2} size="sm" />
                  </Button>
                </div>
              </div>

              {/* Destructive Icon Only */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Destructive</div>
                <div className="flex flex-wrap gap-4">
                  <Button size="icon" variant="destructive">
                    <Icon icon={Trash2} size="sm" />
                  </Button>
                  <Button size="icon" variant="destructive">
                    <Icon icon={X} size="sm" />
                  </Button>
                  <Button size="icon" variant="destructive">
                    <Icon icon={AlertTriangle} size="sm" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Icon Only Buttons - Different Variants */}
            <div className="space-y-2">
              <Label>Кнопки с иконками - разные варианты</Label>
              <div className="flex flex-wrap gap-4">
                <Button size="icon">
                  <Icon icon={Plus} size="sm" />
                </Button>
                <Button size="icon" variant="secondary">
                  <Icon icon={Edit} size="sm" />
                </Button>
                <Button size="icon" variant="destructive">
                  <Icon icon={Trash2} size="sm" />
                </Button>
                <Button size="icon" variant="ghost">
                  <Icon icon={Heart} size="sm" />
                </Button>
                <Button size="icon" variant="ghost">
                  <Icon icon={Share} size="sm" />
                </Button>
                <Button size="icon" variant="ghost">
                  <Icon icon={Bookmark} size="sm" />
                </Button>
              </div>
            </div>

            {/* Action Buttons - All Sizes */}
            <div className="space-y-4">
              <Label>Кнопки действий - все размеры</Label>
              
              {/* Primary Actions */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Primary Actions</div>
                <div className="flex flex-wrap gap-4">
                  <Button size="sm">
                    <Icon icon={Plus} size="sm" className="mr-2" />
                    Add New
                  </Button>
                  <Button size="default">
                    <Icon icon={Plus} size="sm" className="mr-2" />
                    Add New
                  </Button>
                  <Button size="lg">
                    <Icon icon={Plus} size="sm" className="mr-2" />
                    Add New
                  </Button>
                </div>
              </div>

              {/* Secondary Actions */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Secondary Actions</div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="outline" size="sm">
                    <Icon icon={Download} size="sm" className="mr-2" />
                    Export
                  </Button>
                  <Button variant="outline" size="default">
                    <Icon icon={Download} size="sm" className="mr-2" />
                    Export
                  </Button>
                  <Button variant="outline" size="lg">
                    <Icon icon={Download} size="sm" className="mr-2" />
                    Export
                  </Button>
                </div>
              </div>

              {/* Utility Actions */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Utility Actions</div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="outline" size="sm">
                    <Icon icon={Upload} size="sm" className="mr-2" />
                    Import
                  </Button>
                  <Button variant="outline" size="default">
                    <Icon icon={Upload} size="sm" className="mr-2" />
                    Import
                  </Button>
                  <Button variant="outline" size="lg">
                    <Icon icon={Upload} size="sm" className="mr-2" />
                    Import
                  </Button>
                </div>
              </div>

              {/* System Actions */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">System Actions</div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="outline" size="sm">
                    <Icon icon={RefreshCw} size="sm" className="mr-2" />
                    Refresh
                  </Button>
                  <Button variant="outline" size="default">
                    <Icon icon={RefreshCw} size="sm" className="mr-2" />
                    Refresh
                  </Button>
                  <Button variant="outline" size="lg">
                    <Icon icon={RefreshCw} size="sm" className="mr-2" />
                    Refresh
                  </Button>
                </div>
              </div>

              {/* Save Actions */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Save Actions</div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="outline" size="sm">
                    <Icon icon={Save} size="sm" className="mr-2" />
                    Save
                  </Button>
                  <Button variant="outline" size="default">
                    <Icon icon={Save} size="sm" className="mr-2" />
                    Save
                  </Button>
                  <Button variant="outline" size="lg">
                    <Icon icon={Save} size="sm" className="mr-2" />
                    Save
                  </Button>
                </div>
              </div>
            </div>

            {/* Disabled States - All Sizes */}
            <div className="space-y-4">
              <Label>Отключенные состояния - все размеры</Label>
              
              {/* Default Disabled */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Default Disabled</div>
                <div className="flex flex-wrap gap-4">
                  <Button disabled size="sm">
                    <Icon icon={User} size="sm" className="mr-2" />
                    Disabled
                  </Button>
                  <Button disabled size="default">
                    <Icon icon={User} size="sm" className="mr-2" />
                    Disabled
                  </Button>
                  <Button disabled size="lg">
                    <Icon icon={User} size="sm" className="mr-2" />
                    Disabled
                  </Button>
                </div>
              </div>

              {/* Icon Only Disabled */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Icon Only Disabled</div>
                <div className="flex flex-wrap gap-4">
                  <Button size="icon" variant="outline" disabled>
                    <Icon icon={Settings} size="sm" />
                  </Button>
                  <Button size="icon" variant="secondary" disabled>
                    <Icon icon={Mail} size="sm" />
                  </Button>
                  <Button size="icon" variant="ghost" disabled>
                    <Icon icon={Bell} size="sm" />
                  </Button>
                </div>
              </div>

              {/* Ghost Disabled */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Ghost Disabled</div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="ghost" disabled size="sm">
                    <Icon icon={Mail} size="sm" className="mr-2" />
                    Disabled Ghost
                  </Button>
                  <Button variant="ghost" disabled size="default">
                    <Icon icon={Mail} size="sm" className="mr-2" />
                    Disabled Ghost
                  </Button>
                  <Button variant="ghost" disabled size="lg">
                    <Icon icon={Mail} size="sm" className="mr-2" />
                    Disabled Ghost
                  </Button>
                </div>
              </div>
            </div>

            {/* Loading States - All Sizes */}
            <div className="space-y-4">
              <Label>Состояния загрузки - все размеры</Label>
              
              {/* Default Loading */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Default Loading</div>
                <div className="flex flex-wrap gap-4">
                  <Button disabled size="sm">
                    <Icon icon={Loader2} size="sm" className="mr-2 animate-spin" />
                    Loading...
                  </Button>
                  <Button disabled size="default">
                    <Icon icon={Loader2} size="sm" className="mr-2 animate-spin" />
                    Loading...
                  </Button>
                  <Button disabled size="lg">
                    <Icon icon={Loader2} size="sm" className="mr-2 animate-spin" />
                    Loading...
                  </Button>
                </div>
              </div>

              {/* Icon Only Loading */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Icon Only Loading</div>
                <div className="flex flex-wrap gap-4">
                  <Button size="icon" variant="outline" disabled>
                    <Icon icon={Loader2} size="sm" className="animate-spin" />
                  </Button>
                  <Button size="icon" variant="secondary" disabled>
                    <Icon icon={Loader2} size="sm" className="animate-spin" />
                  </Button>
                  <Button size="icon" variant="ghost" disabled>
                    <Icon icon={Loader2} size="sm" className="animate-spin" />
                  </Button>
                </div>
              </div>

              {/* Secondary Loading */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">Secondary Loading</div>
                <div className="flex flex-wrap gap-4">
                  <Button variant="secondary" disabled size="sm">
                    <Icon icon={Loader2} size="sm" className="mr-2 animate-spin" />
                    Processing...
                  </Button>
                  <Button variant="secondary" disabled size="default">
                    <Icon icon={Loader2} size="sm" className="mr-2 animate-spin" />
                    Processing...
                  </Button>
                  <Button variant="secondary" disabled size="lg">
                    <Icon icon={Loader2} size="sm" className="mr-2 animate-spin" />
                    Processing...
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dropdown Menu Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Dropdown Menu</CardTitle>
            <CardDescription>Выпадающие меню с различными вариантами</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Dropdown */}
            <div className="space-y-2">
              <Label>Базовое выпадающее меню</Label>
              <div className="flex flex-wrap gap-4">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">Open Menu</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    <DropdownMenuItem>
                <User className="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Settings</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Mail className="mr-2 h-4 w-4" />
                      <span>Messages</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Log out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Dropdown with Icons */}
            <div className="space-y-2">
              <Label>Выпадающее меню с иконками</Label>
              <div className="flex flex-wrap gap-4">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      <User className="mr-2 h-4 w-4" />
                      Account
              </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    <DropdownMenuItem>
                      <User className="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <CreditCard className="mr-2 h-4 w-4" />
                      <span>Billing</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Settings</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Log out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      <Settings className="mr-2 h-4 w-4" />
                      Options
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    <DropdownMenuItem>
                      <Edit className="mr-2 h-4 w-4" />
                      <span>Edit</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Copy className="mr-2 h-4 w-4" />
                      <span>Copy</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Share className="mr-2 h-4 w-4" />
                      <span>Share</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <Trash2 className="mr-2 h-4 w-4" />
                      <span>Delete</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Icon Only Dropdown */}
            <div className="space-y-2">
              <Label>Выпадающее меню только с иконкой</Label>
              <div className="flex flex-wrap gap-4">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="icon" variant="outline">
                      <User className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    <DropdownMenuItem>
                      <User className="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Settings</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Log out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="icon" variant="outline">
                      <Settings className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    <DropdownMenuItem>
                      <Edit className="mr-2 h-4 w-4" />
                      <span>Edit</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Copy className="mr-2 h-4 w-4" />
                      <span>Copy</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Share className="mr-2 h-4 w-4" />
                      <span>Share</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <Trash2 className="mr-2 h-4 w-4" />
                      <span>Delete</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="icon" variant="outline">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    <DropdownMenuItem>
                      <Download className="mr-2 h-4 w-4" />
                      <span>Download</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Upload className="mr-2 h-4 w-4" />
                      <span>Upload</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Save className="mr-2 h-4 w-4" />
                      <span>Save</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      <span>Refresh</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Dropdown with Shortcuts */}
            <div className="space-y-2">
              <Label>Выпадающее меню с горячими клавишами</Label>
              <div className="flex flex-wrap gap-4">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">File Menu</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    <DropdownMenuItem>
                      <FileText className="mr-2 h-4 w-4" />
                      <span>New File</span>
                      <DropdownMenuShortcut>⌘N</DropdownMenuShortcut>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <FolderOpen className="mr-2 h-4 w-4" />
                      <span>Open</span>
                      <DropdownMenuShortcut>⌘O</DropdownMenuShortcut>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Save className="mr-2 h-4 w-4" />
                      <span>Save</span>
                      <DropdownMenuShortcut>⌘S</DropdownMenuShortcut>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <Printer className="mr-2 h-4 w-4" />
                      <span>Print</span>
                      <DropdownMenuShortcut>⌘P</DropdownMenuShortcut>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Dropdown with Disabled Items */}
            <div className="space-y-2">
              <Label>Выпадающее меню с отключенными элементами</Label>
              <div className="flex flex-wrap gap-4">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">Actions</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    <DropdownMenuItem>
                      <Edit className="mr-2 h-4 w-4" />
                      <span>Edit</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem disabled>
                      <Copy className="mr-2 h-4 w-4" />
                      <span>Copy (Disabled)</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Share className="mr-2 h-4 w-4" />
                      <span>Share</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem disabled>
                      <Trash2 className="mr-2 h-4 w-4" />
                      <span>Delete (Disabled)</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Dropdown with Submenu */}
            <div className="space-y-2">
              <Label>Выпадающее меню с подменю</Label>
              <div className="flex flex-wrap gap-4">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">More Options</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    <DropdownMenuItem>
                      <User className="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Settings</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Users className="mr-2 h-4 w-4" />
                        <span>Team</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        <DropdownMenuItem>
                          <UserPlus className="mr-2 h-4 w-4" />
                          <span>Invite User</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <UserMinus className="mr-2 h-4 w-4" />
                          <span>Remove User</span>
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Log out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Dashboard Style Dropdown */}
            <div className="space-y-2">
              <Label>Стиль дашборда (как в приложении)</Label>
              <div className="flex flex-wrap gap-4">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Icon icon={Columns} size="sm" className="mr-2" />
                      <span className="hidden lg:inline">Customize Columns</span>
                      <span className="lg:hidden">Columns</span>
                      <Icon icon={ChevronDown} size="sm" className="ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    {["Name", "Email", "Status", "Amount"].map((col) => (
                      <DropdownMenuItem
                        key={col}
                        onClick={() => {
                          setSelectedColumns((prev) =>
                            prev.includes(col)
                              ? prev.filter((c) => c !== col)
                              : [...prev, col]
                          );
                        }}
                      >
                        {selectedColumns.includes(col) && <Check className="mr-2 h-4 w-4 text-primary" />}
                        {!selectedColumns.includes(col) && <span className="mr-2 h-4 w-4 inline-block" />}
                        <span>{col}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cards Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Cards</CardTitle>
            <CardDescription>Карточки для отображения контента</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {sampleData.map((item, index) => (
                <Card key={index}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      {item.name}
                    </CardTitle>
                    <item.icon className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{item.total}</div>
                    <p className="text-xs text-muted-foreground">
                      {item.change} from last month
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Form Elements */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Form Elements</CardTitle>
            <CardDescription>Элементы форм</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="Enter your email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" placeholder="Enter your name" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Input id="message" placeholder="Enter your message" />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="terms" 
                checked={isChecked}
                onCheckedChange={(checked) => setIsChecked(checked === true)}
              />
              <Label htmlFor="terms">Accept terms and conditions</Label>
            </div>
            <div className="space-y-2">
              <Label>Select Option</Label>
              <Select value={selectedOption} onValueChange={setSelectedOption}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="option1">Option 1</SelectItem>
                  <SelectItem value="option2">Option 2</SelectItem>
                  <SelectItem value="option3">Option 3</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Badges and Alerts */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Badges & Alerts</CardTitle>
            <CardDescription>Бейджи и уведомления</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge>Default</Badge>
              <Badge variant="secondary">Secondary</Badge>
              <Badge variant="destructive">Destructive</Badge>
              <Badge variant="outline">Outline</Badge>
            </div>
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                This is a default alert with an info icon.
              </AlertDescription>
            </Alert>
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                This is a destructive alert with an error icon.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Interactive Elements */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Interactive Elements</CardTitle>
            <CardDescription>Интерактивные элементы</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-4">
              <Toggle 
                pressed={isToggled}
                onPressedChange={setIsToggled}
                aria-label="Toggle bold"
              >
                <Bold className="h-4 w-4" />
              </Toggle>
              <span>Toggle: {isToggled ? 'On' : 'Off'}</span>
            </div>
            <div className="space-y-2">
              <Label>Progress: {progress}%</Label>
              <Progress value={progress} className="w-full" />
            </div>
            <Separator />
            <div className="flex items-center space-x-4">
              <Avatar>
                <AvatarImage src="https://github.com/shadcn.png" />
                <AvatarFallback>CN</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium">User Name</p>
                <p className="text-sm text-muted-foreground">user@example.com</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Switch */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Switch</CardTitle>
            <CardDescription>Переключатель вкл/выкл</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch id="airplane-mode" />
              <Label htmlFor="airplane-mode">Airplane Mode</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch id="wifi" defaultChecked />
              <Label htmlFor="wifi">Wi-Fi</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch id="bluetooth" disabled />
              <Label htmlFor="bluetooth">Bluetooth</Label>
            </div>
          </CardContent>
        </Card>

        {/* Slider */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Slider</CardTitle>
            <CardDescription>Ползунок для выбора значений</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="volume">Volume</Label>
              <Slider
                id="volume"
                defaultValue={[33]}
                max={100}
                step={1}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="brightness">Brightness</Label>
              <Slider
                id="brightness"
                defaultValue={[50]}
                max={100}
                step={1}
                className="w-full"
              />
            </div>
          </CardContent>
        </Card>

        {/* Radio Group */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Radio Group</CardTitle>
            <CardDescription>Группа радиокнопок</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <RadioGroup defaultValue="option-one">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="option-one" id="option-one" />
                <Label htmlFor="option-one">Option One</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="option-two" id="option-two" />
                <Label htmlFor="option-two">Option Two</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="option-three" id="option-three" />
                <Label htmlFor="option-three">Option Three</Label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Textarea */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Textarea</CardTitle>
            <CardDescription>Многострочное текстовое поле</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Type your message here."
                className="min-h-[100px]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter a description..."
                className="min-h-[80px]"
              />
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Pagination</CardTitle>
            <CardDescription>Навигация по страницам</CardDescription>
          </CardHeader>
          <CardContent>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious onClick={(e) => e.preventDefault()} />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink onClick={(e) => e.preventDefault()} isActive>
                    1
                  </PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink onClick={(e) => e.preventDefault()}>2</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink onClick={(e) => e.preventDefault()}>3</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext onClick={(e) => e.preventDefault()} />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </CardContent>
        </Card>

        {/* Resizable */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Resizable</CardTitle>
            <CardDescription>Изменяемые размеры панелей</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[200px] w-full rounded-md border">
              <ResizablePanelGroup direction="horizontal">
                <ResizablePanel defaultSize={50}>
                  <div className="flex h-full items-center justify-center p-6">
                    <span className="font-semibold">Panel 1</span>
                  </div>
                </ResizablePanel>
                <ResizableHandle withHandle />
                <ResizablePanel defaultSize={50}>
                  <div className="flex h-full items-center justify-center p-6">
                    <span className="font-semibold">Panel 2</span>
                  </div>
                </ResizablePanel>
              </ResizablePanelGroup>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Tabs</CardTitle>
            <CardDescription>Вкладки для организации контента</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="account" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="account">Account</TabsTrigger>
                <TabsTrigger value="password">Password</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>
              <TabsContent value="account" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" placeholder="Enter your name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input id="username" placeholder="Enter your username" />
                </div>
              </TabsContent>
              <TabsContent value="password" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current">Current password</Label>
                  <Input id="current" type="password" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new">New password</Label>
                  <Input id="new" type="password" />
                </div>
              </TabsContent>
              <TabsContent value="settings" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="theme">Theme</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Select theme" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                      <SelectItem value="system">System</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Dialog */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Dialog</CardTitle>
            <CardDescription>Модальные окна</CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">Open Dialog</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Edit profile</DialogTitle>
                  <DialogDescription>
                    Make changes to your profile here. Click save when you're done.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="name" className="text-right">
                      Name
                    </Label>
                    <Input
                      id="name"
                      defaultValue="Pedro Duarte"
                      className="col-span-3"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="username" className="text-right">
                      Username
                    </Label>
                    <Input
                      id="username"
                      defaultValue="@peduarte"
                      className="col-span-3"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline">Cancel</Button>
                  <Button>Save changes</Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        {/* Tooltips and Popovers */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Tooltips & Popovers</CardTitle>
            <CardDescription>Всплывающие подсказки и меню</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline">Hover for tooltip</Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>This is a tooltip</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline">Click for popover</Button>
              </PopoverTrigger>
              <PopoverContent className="w-80">
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <h4 className="font-medium leading-none">Dimensions</h4>
                    <p className="text-sm text-muted-foreground">
                      Set the dimensions for the layer.
                    </p>
                  </div>
                  <div className="grid gap-2">
                    <div className="grid grid-cols-3 items-center gap-4">
                      <Label htmlFor="width">Width</Label>
                      <Input
                        id="width"
                        defaultValue="100%"
                        className="col-span-2 h-8"
                      />
                    </div>
                    <div className="grid grid-cols-3 items-center gap-4">
                      <Label htmlFor="maxWidth">Max. width</Label>
                      <Input
                        id="maxWidth"
                        defaultValue="300px"
                        className="col-span-2 h-8"
                      />
                    </div>
                    <div className="grid grid-cols-3 items-center gap-4">
                      <Label htmlFor="height">Height</Label>
                      <Input
                        id="height"
                        defaultValue="25px"
                        className="col-span-2 h-8"
                      />
                    </div>
                    <div className="grid grid-cols-3 items-center gap-4">
                      <Label htmlFor="maxHeight">Max. height</Label>
                      <Input
                        id="maxHeight"
                        defaultValue="none"
                        className="col-span-2 h-8"
                      />
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </CardContent>
        </Card>

        {/* Loading States */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Loading States</CardTitle>
            <CardDescription>Состояния загрузки</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <SimpleLoadingSpinner />
            <div className="space-y-2">
              <Skeleton className="h-4 w-[250px]" />
              <Skeleton className="h-4 w-[200px]" />
              <Skeleton className="h-4 w-[300px]" />
            </div>
          </CardContent>
        </Card>

        {/* Auth Modal */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Auth Modal</CardTitle>
            <CardDescription>Модальное окно аутентификации</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setIsAuthModalOpen(true)}>
              Open Auth Modal
            </Button>
            {/* AuthModal temporarily disabled for testing */}
            {/* <AuthModal 
              isOpen={isAuthModalOpen} 
              onClose={() => setIsAuthModalOpen(false)} 
              defaultMode="login"
            /> */}
          </CardContent>
        </Card>

        {/* Footer */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Footer</CardTitle>
            <CardDescription>Подвал сайта</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Footer temporarily disabled for testing */}
            {/* <Footer /> */}
            <div className="p-4 border rounded">
              <p className="text-muted-foreground">Footer Component</p>
            </div>
          </CardContent>
        </Card>

        {/* Icons Showcase */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Icons</CardTitle>
            <CardDescription>Демонстрация иконок Lucide React</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-8 md:grid-cols-12 lg:grid-cols-16 gap-4">
              {[
                Home, User, Settings, Search, Filter, Bell, Mail, Star,
                Heart, Share, Bookmark, Clock, MapPin, Phone, Globe, Lock,
                Shield, Zap, Target, BarChart3, Activity, Users, CreditCard, 
                Wallet, Building2, Car, HomeIcon, Briefcase, GraduationCap, 
                Baby, Plane, Camera, Music, Utensils, ShoppingBag, Gift, 
                HeartIcon, Smile, ThumbsUp, MessageCircle, Send, Edit, Trash2, 
                Copy, Link, ExternalLink, Maximize2, Minimize2, RotateCcw, 
                RefreshCw, Play, Pause, SkipBack, SkipForward, Volume2, 
                VolumeX, Mic, MicOff, Video, VideoOff, Wifi, WifiOff, 
                Battery, Moon, Sun, Cloud, CloudRain, CloudSnow, Wind, 
                Thermometer, Umbrella, Snowflake, Droplets, Waves, Mountain, 
                Leaf, Flower, Bug, Bird, Fish, Cat, Dog
              ].map((Icon, index) => (
                <div key={index} className="flex flex-col items-center p-2 border rounded hover:bg-muted">
                  <Icon className="h-6 w-6 mb-1" />
                  <span className="text-xs text-center">{Icon.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Summary</CardTitle>
            <CardDescription>Статистика компонентов</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">25+</div>
                <div className="text-sm text-muted-foreground">UI Components</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">7</div>
                <div className="text-sm text-muted-foreground">Languages</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">100+</div>
                <div className="text-sm text-muted-foreground">Icons</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <Toaster />
    </div>
  );
};

export default ComponentsShowcase; 